import { lazy, Suspense, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigationType } from 'react-router-dom';
import AppLayout from '../components/layout/AppLayout.jsx';
import LoadingState from '../components/common/LoadingState.jsx';
import { useAppData } from '../contexts/AppDataContext.jsx';
import { trackPageView } from '../services/analyticsService.js';
import CartPage from '../pages/cart/CartPage.jsx';
import AboutPage from '../pages/content/AboutPage.jsx';
import ContactPage from '../pages/content/ContactPage.jsx';
import FaqPage from '../pages/content/FaqPage.jsx';
import GalleryPage from '../pages/content/GalleryPage.jsx';
import ServicesPage from '../pages/content/ServicesPage.jsx';
import CategoryPage from '../pages/storefront/CategoryPage.jsx';
import HomePage from '../pages/storefront/HomePage.jsx';
import ProductPage from '../pages/storefront/ProductPage.jsx';
import SearchPage from '../pages/storefront/SearchPage.jsx';
import NotFoundPage from '../pages/system/NotFoundPage.jsx';

/* Heavy, rarely-first-hit sections load as separate chunks: the admin
   panel (its own app + admin.css), checkout (Square SDK glue), and the
   account flows. Everything customer-facing on the landing path stays
   in the main bundle. */
const AdminApp = lazy(() => import('../pages/admin/AdminApp.jsx'));
const CheckoutPage = lazy(() => import('../pages/checkout/CheckoutPage.jsx'));
const AccountPage = lazy(() => import('../pages/account/AccountPage.jsx'));

/* Reset scroll to the top on every forward navigation. Browser
   back/forward (POP) keeps its restored position so the back button
   still returns the user to where they were. */
function ScrollToTop() {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    if (navigationType !== 'POP') {
      window.scrollTo(0, 0);
    }
  }, [pathname, navigationType]);

  return null;
}

/* One analytics beacon per rendered route. Fires after paint so it never
   delays the page, and skips /admin (see trackPageView). */
function TrackPageViews() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    const id = window.setTimeout(() => trackPageView({ path: pathname }), 0);
    return () => window.clearTimeout(id);
  }, [pathname, search]);

  return null;
}

export default function App() {
  const { status, error } = useAppData();

  if (status === 'loading') {
    return <LoadingState label="Loading La Bella Trading" />;
  }

  if (status === 'error') {
    return (
      <div className="container page-block">
        <h1>Could not load the storefront</h1>
        <p>{error?.message || 'Storefront data could not load.'}</p>
      </div>
    );
  }

  return (
    <>
      <ScrollToTop />
      <TrackPageViews />
      <Suspense fallback={<LoadingState label="Loading La Bella Trading" />}>
      <Routes>
      <Route path="/admin/*" element={<AdminApp />} />
      <Route element={<AppLayout />}>
        <Route index element={<HomePage />} />
        <Route path="/category/:type/:id" element={<CategoryPage />} />
        <Route path="/product/:id" element={<ProductPage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/cart" element={<CartPage />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/gallery" element={<GalleryPage />} />
        <Route path="/faq" element={<FaqPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/login" element={<AccountPage type="login" />} />
        <Route path="/register" element={<AccountPage type="register" />} />
        <Route path="/registration" element={<AccountPage type="register" />} />
        <Route path="/forgot-password" element={<AccountPage type="forgot" />} />
        <Route path="/forget-password" element={<AccountPage type="forgot" />} />
        <Route path="/reset-password" element={<AccountPage type="reset" />} />
        <Route path="/verify-email" element={<AccountPage type="verify" />} />
        <Route path="/dashboard" element={<AccountPage type="account" />} />
        <Route path="/customer-order" element={<AccountPage type="orders" />} />
        <Route path="/customer-profile-update" element={<AccountPage type="profile" />} />
        <Route path="/customer-billing-shipping-update" element={<Navigate to="/customer-profile-update" replace />} />
        <Route path="/customer-password-update" element={<AccountPage type="password" />} />
        <Route path="/payment-success" element={<AccountPage type="paymentSuccess" />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
      </Routes>
      </Suspense>
    </>
  );
}
